import { z } from 'zod';

// Step Auto-Advance Types
export const ZStepAutoAdvance = z.object({
  enabled: z.boolean(),
  trigger: z.enum(['fieldChange', 'postMessage']).optional(),
  eventType: z.string().optional(),
  delay: z.number().optional(),
});

export type IStepAutoAdvance = z.infer<typeof ZStepAutoAdvance>;

// Form Step Types
export const ZFormStep = z.object({
  id: z.string(),
  title: z.string().optional(),
  description: z.string().optional(),
  fields: z.array(z.string()),
  autoAdvance: ZStepAutoAdvance.optional(),
});

export type IFormStep = z.infer<typeof ZFormStep>;

// Form Theme Types
export const ZFormTheme = z.object({
  primaryColor: z.string().optional(),
  fontFamily: z.string().optional(),
  borderRadius: z.string().optional(),
});

export type IFormTheme = z.infer<typeof ZFormTheme>;

// Form Schema Types
export const ZFormSchema = z.object({
  title: z.string(),
  type: z.literal('object'),
  properties: z.record(z.string(), z.unknown()),
  required: z.array(z.string()).optional(),
  uiSchema: z.record(z.string(), z.unknown()).optional(),
  steps: z.array(ZFormStep).optional(),
  theme: ZFormTheme.optional(),
  completionAction: z
    .object({
      type: z.enum(['redirect', 'message', 'both']),
      url: z.string().optional(),
      message: z.string().optional(),
    })
    .optional(),
});

export type IFormSchema = z.infer<typeof ZFormSchema>;

/**
 * Validates cross-field consistency in a form schema.
 * Catches issues that Zod structural validation cannot (e.g., fields missing from layout grid).
 */
export function validateSchemaConsistency(schema: IFormSchema): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  const propertyNames = Object.keys(schema.properties || {});

  // Check LayoutGridField coverage: every property must appear in exactly one ui:col
  const uiSchema = schema.uiSchema as Record<string, unknown> | undefined;
  if (uiSchema?.['ui:field'] === 'LayoutGridField') {
    const layoutGrid = uiSchema['ui:layoutGrid'] as Record<string, unknown> | undefined;
    const row = layoutGrid?.['ui:row'] as Record<string, unknown> | undefined;
    const children = (row?.children || []) as Array<{ 'ui:col'?: { children?: string[] } }>;

    const gridFields = new Set<string>();
    for (const col of children) {
      for (const field of col['ui:col']?.children || []) {
        gridFields.add(field);
      }
    }

    const missingFromGrid = propertyNames.filter((f) => !gridFields.has(f));
    if (missingFromGrid.length > 0) {
      errors.push(
        `LayoutGridField is missing fields from ui:layoutGrid: ${missingFromGrid.join(', ')}. Every field in "properties" must appear in exactly one "ui:col".`,
      );
    }

    const extraInGrid = [...gridFields].filter((f) => !propertyNames.includes(f));
    if (extraInGrid.length > 0) {
      errors.push(`LayoutGridField references fields not in "properties": ${extraInGrid.join(', ')}.`);
    }
  }

  // Check steps coverage: every property should be in exactly one step
  if (schema.steps && schema.steps.length > 0) {
    const steppedFields = new Set<string>();
    for (const step of schema.steps) {
      for (const field of step.fields) {
        steppedFields.add(field);
      }
    }

    const missingFromSteps = propertyNames.filter((f) => !steppedFields.has(f));
    if (missingFromSteps.length > 0) {
      errors.push(
        `Steps are missing fields from "properties": ${missingFromSteps.join(', ')}. Every field must be assigned to exactly one step.`,
      );
    }

    const extraInSteps = [...steppedFields].filter((f) => !propertyNames.includes(f));
    if (extraInSteps.length > 0) {
      errors.push(`Steps reference fields not in "properties": ${extraInSteps.join(', ')}.`);
    }
  }

  return { valid: errors.length === 0, errors };
}
